import { AutogenLayoutProspect } from "./autogen/AutogenLayoutProspect";
import { BlurEvent, Button, ClickEvent, Component, Location, CrudDecorator, DataSourceExecutionEvent, DataSourceMode, DecoratorFactory, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, SlideoutDecorator, TableRowDisplayEvent, DataSource, AddType, OpenType } from "@mcleod/components";
import { Alignment, Api, ArrayUtil, CurrencySettings, DateUtil, ModelRow, StringUtil } from "@mcleod/core";
import { AssignMarketPlan } from "./AssignMarketPlan";
import { UsersResponsibilitySlideoutMF } from "@mcleod/general/src/UsersResponsibilitySlideoutMF";
import { DuplicateId, DuplicateIdProps, OrderTabView } from "@mcleod/dispatch";
import { QuickQuote } from "@mcleod/powerbroker/src/QuickQuote";
import { OrderList } from "@mcleod/dispatch/src/OrderList";
import { GoogleSearchDecorator, GoogleSearchDecoratorProps } from "@mcleod/dispatch/src/GoogleSearchDecorator";
import { ProspectFindNear } from "./ProspectFindNear";
import { ProspectToLocation } from "./ProspectToLocation";
import { LMEUserSettings } from "@mcleod/general";

const GOOGLE_SEARCH_PROPS: GoogleSearchDecoratorProps = {
    cityField: 'city',
    cityIdField: 'city_id',
    stateField: 'state_id',
    zipField: 'zip_code'
}

const viewProspectURL = "lme/crm/Prospect?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "customer",
    screenName: "Prospect",
    navigateToUrl: viewProspectURL
};

export class Prospect extends AutogenLayoutProspect {
    codeGenerationIsActive: boolean;
    allowManualEntry: boolean;

    override async onLoad() {
        this.sourceProspect.afterGetDataboundValues = (row: ModelRow, dataSource: DataSource) => this.sourceProspectAfterGetDataboundValues(row, dataSource);
        this.layoutFinancials.textboxCreditStatus.required = false;
        this.layoutQuoteList.tableQuoteList.addType = AddType.NOT_ALLOWED
        const googleSearchDecorator = new GoogleSearchDecorator(this, GOOGLE_SEARCH_PROPS);
        this.tableOpportunity.defaultOpenType = OpenType.NEW_TAB_WITH_SWITCH;
        this.layoutContactTable.parentRowType = "C";
        this.layoutContactTable.showRecurringContactColumns = true;
        this.layoutContactTable.defaultTypeOf = 'P';
        const allContacts = 'Y' === LMEUserSettings.get()?.all_contacts;
        if (!allContacts) {
            this.sourceContacts.addBeforeExecutionListener((event) => {
                event.filter.type_of = "P || B";
            });
        }
    }

    sourceProspectAfterGetDataboundValues(row: ModelRow, dataSource: DataSource) {
        if (dataSource.mode === DataSourceMode.SEARCH) {
            //have to set primary_agency in search mode because the field isn't bound
            const primaryAgencyField = this.getCustomerUsersAgencyField();
            row.set(primaryAgencyField, dataSource.searchRow.get(primaryAgencyField));

            //have to add agency_link search data to the filter manually, since there are no bound components for those records
            if (ArrayUtil.isEmptyArray(this.sourceAgencyLink.data))
                return;
            row.addLinkedModel({ model: this.sourceAgencyLink.url, rows: [this.sourceAgencyLink.data[0]] });
        }
    }

    textboxPrimaryContactBeforeLookupModelSearch(event) {
        event.filter.parent_row_id = this.activeRow?.get("id", -1);
        event.filter.parent_row_type = "C";
    }


    textboxReassignProfileIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        this.setByDates(true);

    }

    setByDates(redisplay = false) {
        const lookupData = this.mainDataSource.activeRow.getFirstLookupModelData("reassign_profile_id")
        const activityStartDate = this.activeRow?.getDate(`activity_start_date`, null) as Date;
        const orderCountStartDate = this.activeRow?.getDate(`order_start_date`, null);
        const revenueStartDate = this.activeRow?.getDate(`revenue_start_date`, null);
        if (activityStartDate) {
            const daysToAdd = parseInt(lookupData.get(`activity_days`, 0));
            activityStartDate.setDate(activityStartDate.getDate() + daysToAdd);
            const parsedDate = DateUtil.parseDateTime(activityStartDate);
            this.textboxByActivity.text =  DateUtil.formatDate(parsedDate, "MM/dd/yyyy");
        }
        else {
            this.textboxByActivity.text = "";
        }
        if (orderCountStartDate) {
            const daysToAdd = parseInt(lookupData.get(`order_days`, 0));
            orderCountStartDate.setDate(orderCountStartDate.getDate() + daysToAdd);
            const parsedDate = DateUtil.parseDateTime(orderCountStartDate);
            this.textboxByOrder.text =  DateUtil.formatDate(parsedDate, "MM/dd/yyyy");
        }
        else {
            this.textboxByOrder.text = "";
        }
        if (revenueStartDate) {
            const daysToAdd = parseInt(lookupData.get(`revenue_days`, 0));
            revenueStartDate.setDate(revenueStartDate.getDate() + daysToAdd);
            const parsedDate = DateUtil.parseDateTime(revenueStartDate);
            this.textboxByRevenue.text =  DateUtil.formatDate(parsedDate, "MM/dd/yyyy");
        }
        else {
            this.textboxByRevenue.text = "";
        }
        if (redisplay) {
            this.mainDataSource.activeRow.set("salesperson_reassign.activity_count", lookupData.get("activity_count", 0));
            this.mainDataSource.activeRow.set("salesperson_reassign.order_count", lookupData.get("order_count", 0));
            this.mainDataSource.activeRow.set("salesperson_reassign.revenue", lookupData.get("revenue", 0));
        }
    }

    sourceProspectAfterExecution(event: DataSourceExecutionEvent) {
        this.layoutTableActivities.prospectId = this.activeRow.get("id");
        this.dataHeader.showDelete = this.activeRow.get("type_of") == "P";
        if (this.mainDataSource.activeRow.get("type_of") === "C") {
            this.textboxTypeOf.enabled = false;
        }
        this.setByDates();
        this.layoutQuoteList.customerName = this.activeRow.get("name", null);
        this.buttonAddOrder.enabled = this.mainDataSource.activeRow.get("type_of") === "C";
        this.tabFinancials.visible = this.mainDataSource.activeRow.get("type_of") === "C";
        this.tabCreditCollections.visible = this.mainDataSource.activeRow.get("type_of") === "C";
        this.buttonSearchOrders.enabled = this.mainDataSource.activeRow.get("type_of") === "C";
        const currencySettings = CurrencySettings.getSingleton();
        this.layoutCreditCollections.sourceCreditCalculations.search({
            customer_id: this.sourceProspect.activeRow?.get("id"),
            currency_type: this.sourceProspect.activeRow?.get("currency_type", currencySettings.getTransactionalCurrency())
        });
    }

    buttonMarketPlanOnClick(event: ClickEvent) {
        const payeeId = this.mainDataSource.activeRow.get("id");
        AssignMarketPlan.showModal(payeeId, null, () => {
            this.mainDataSource.search(this.mainDataSource.lastSearch);
        });
    }

    textboxOperationsUserBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.web_user_type = "in U,E";
    }

    buttonAddOrderOnClick(event: ClickEvent) {
        const id = this.sourceProspect.activeRow?.get("id", null);
        const name = this.sourceProspect.activeRow?.get("name", null);
        const layout = Layout.getLayout("lme/dispatch/OrderTabView", { displayingFromToolbox: true }) as OrderTabView;
        layout.addLayoutLoadListener(event => {
            const decorated: Component = DecoratorFactory.decoratePage(layout, null);
            if (decorated instanceof CrudDecorator) {
                decorated.headerProps = { showClose: true };
                decorated.slideIn({ speed: 200 }, null, null, { paddingAll: 8 });
            }
            layout.layoutOrders.sourceOrders.addAfterModeChangeListener(async event => {
                const lmData = {
                    id: id,
                    name: name,
                };
                layout.layoutOrders.sourceOrders.activeRow.setValueAndLookupModelData("customer_id", id, lmData);
            });
        });
    }

    buttonAddQuoteOnClick(event: ClickEvent) {
        const id = this.sourceProspect.activeRow?.get("id", null);
        const name = this.sourceProspect.activeRow?.get("name", null);
        const contactLookupData = this.sourceProspect.activeRow?.getFirstLookupModelData("primary_contact");
        const contactName = contactLookupData?.get("name", null);
        const layout = Layout.getLayout("lme/powerbroker/QuickQuote", { displayingFromToolbox: true }) as QuickQuote;
        layout.addLayoutLoadListener(event => {
            const decorated: Component = DecoratorFactory.decoratePage(layout, null);
            if (decorated instanceof CrudDecorator) {
                decorated.doAfterSlideOut = async () => {
                    this.mainDataSource.search(this.mainDataSource.lastSearch);
                };
                decorated.headerProps = { showClose: true };
                decorated.slideIn({ speed: 200 }, null, null, { paddingAll: 8 });
            }
            layout.sourceQuoteOrder.addAfterModeChangeListener(async event => {
                 const customerLmData = new ModelRow(layout.textboxCustomerName.lookupModel, true, {
                    id: id,
                    name: name,
                });
                layout.sourceQuoteOrder.activeRow.setValueAndLookupModelData("customer_name", id, customerLmData);
                layout.layoutQuoteStopTable.tableStop.addRowDisplayListener((event: TableRowDisplayEvent) => {
                    const tableRow = event.getTableRow();
                    const location = tableRow["layoutQuoteStop"].locationStop as Location;
                    if (StringUtil.isEmptyString(location.googlePlacesProps["customerId"])) {
                        location.googlePlacesProps["customerId"] = this.mainDataSource.activeRow?.get("id");
                    }
                });
                layout.handleCustomerNameSelection(customerLmData);
                layout.sourceQuoteOrder.activeRow.setValueAndLookupModelData("customer_contact_name", contactName, contactLookupData);
                layout.handleContactNameSelection(contactLookupData);
            });
        });
    }

    buttonSearchOrdersOnClick(event: ClickEvent) {
        const id = this.sourceProspect.activeRow?.get("id", null);
        const name = this.sourceProspect.activeRow?.get("name", null);
        const layout = Layout.getLayout("lme/dispatch/OrderList", { displayingFromToolbox: true }) as OrderList;
        layout.addLayoutLoadListener(event => {
            const decorated = DecoratorFactory.decoratePage(layout, null) as Layout;
            new SlideoutDecorator({
                layout: decorated,
                title: "Orders",
                transitionOptions: { direction: Alignment.BOTTOM },
                displayOverlay: true,
                overlayProps: { closeOnClickOff: false, coverPageHeader: false },
                fillHorizontalSpace: true,
                fillVerticalSpace: true
            });
            const lmData = {
                    id: id,
                    name: name,
                };
            layout.sourceOrdersList.searchRow.setValueAndLookupModelData("customer_id", id, lmData);
            const today = new Date();
            const threeSixty = new Date();
            threeSixty.setDate(today.getDate() - 360);
            const dateRange = DateUtil.formatDate(threeSixty, "MM/dd/yyyy") + " - " + DateUtil.formatDate(today, "MM/dd/yyyy");
            layout.sourceOrdersList.searchRow.set("ordered_date", dateRange);
            layout.panelOrderSearch.displayData(layout.sourceOrdersList.searchRow, null, 0);
            layout.sourceOrdersList.executeValidatedSearch();
        });
    }

    buttonFindNearOnClick(event: ClickEvent) {
        const cityId = this.sourceProspect.activeRow?.get("city_id", null);
        const city = this.sourceProspect.activeRow?.get("search_city", null);
        const stateId = this.sourceProspect.activeRow?.get("state_id", null);
        const zip = this.sourceProspect.activeRow?.get("search_zip_code", null);
        const layout = Layout.getLayout("lme/crm/ProspectFindNear", { displayingFromToolbox: true }) as ProspectFindNear;
        layout.addLayoutLoadListener(event => {
            const decorated = DecoratorFactory.decoratePage(layout, null) as Layout;
            new SlideoutDecorator({
                layout: decorated,
                title: "Find Near Prospects",
                transitionOptions: { direction: Alignment.BOTTOM },
                displayOverlay: true,
                overlayProps: { closeOnClickOff: false, coverPageHeader: false },
                fillHorizontalSpace: true,
                fillVerticalSpace: true
            });
            layout.layoutheaderProsFindNear.visible = false;
            const lmData = {
                find_near_city_id: cityId,
                find_near_city: city,
                find_near_state: stateId,
                find_near_zip: zip
            };
            layout.mainDataSource.searchRow.setValueAndLookupModelData("find_near_city_id", cityId, lmData);
            layout.mainDataSource.searchRow.set("find_near_city_id", cityId);
            layout.mainDataSource.searchRow.set("find_near_city", city);
            layout.mainDataSource.searchRow.set("find_near_state", stateId);
            layout.mainDataSource.searchRow.set("find_near_zip", zip);
            layout.citystateNear.displayData(layout.mainDataSource.searchRow, null, 0);
        });
    }

    buttonMakeLocationOnClick (event: ClickEvent) {
        ProspectToLocation.showAsSlideout([this.mainDataSource.activeRow], this.buttonMakeLocation);
    }


    textboxActivityStartDateOnBlur(event: BlurEvent) {
        this.setByDates();
    }

    sourceProspectAfterModeChange(event) {
        this.dataHeader.addlPanelLeft.forEveryChildComponent((comp) => {
            if (comp instanceof Button) {
                const shouldDisplay = event.newMode !== DataSourceMode.SEARCH && event.newMode !== DataSourceMode.ADD;
                comp.visible = shouldDisplay;
            }
        });
        this.tabFinancials.visible = this.tabCreditCollections.visible = event.newMode !== DataSourceMode.ADD && this.mainDataSource.activeRow.get("type_of") === "C";
    }

    textboxCategoryBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.customer = "Y";
    }

    textboxPrimaryTrailerTypeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.applies_to = "L";
    }

    textboxSalespersonIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
       const data = event.selection as ModelRow;
       const lmData = {
           id: data?.get("manager_id", ""),
           name: data?.get("manager_name", ""),
       };
       this.activeRow.setValueAndLookupModelData("sales_manager_id", data?.get("manager_id", ""), lmData);
    }

    textboxMarkPlanIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.type_of = "C";
    }

    displayCustomerUsersSlideout() {
        const urs = UsersResponsibilitySlideoutMF.builder()
                    .layoutName("lme/crm/ProspectUsers")
                    .title(this.getCustomersUsersSlideoutTitle())
                    .idFilter(this.getCustomerUsersIdFilter())
                    .parentMainDataSource(this.sourceProspect)
                    .parentAgencyLinkDataSource(this.sourceAgencyLink)
                    .primaryAgencyField(this.getCustomerUsersAgencyField())
                    .build();
        urs.show();
    }

    private getCustomersUsersSlideoutTitle(): string {
            const name = this.sourceProspect.activeRow?.get("name");
            return "Prospect Users" + ((StringUtil.isEmptyString(name) === false) ? (" - " + name) : "");
    }

    private getCustomerUsersIdFilter(): any {
        const id = this.sourceProspect.activeRow?.get("id");
        if (id != null)
            return { id: id };
        return null;
    }

    private getCustomerUsersAgencyField(): string {
        return "primary_agency";
    }

    sourceProspectBeforeModeChange(event) {
        if (event.newMode === DataSourceMode.ADD) {
            this.textboxId.enabledDuringAdd = true;
            Api.post("lme/general/code-generation-settings", { "table_name": "customer" }).then(response => {
                this.codeGenerationIsActive = response.data[0]?.is_active ?? false;
                this.allowManualEntry = response.data[0]?.allow_manual_entry ?? false;
                if (this.codeGenerationIsActive) {
                    this.textboxId.caption = "*Code";
                    this.textboxId.enabledDuringAdd = this.allowManualEntry;
                }
                DuplicateId.configureIdTextbox(this.textboxId, {
                    ...DUPLICATE_VALIDATION_PROPS,
                    codeGenerationIsActive: this.codeGenerationIsActive
                });
            });
        }
    }
}
